// The first-person cockpit (flightsim-b62, roadmap #47): press C and you are
// in the seat. The art/ offices (model-integration.md 1h) were built for
// exactly this view: each file's origin IS the pilot's eye, its windscreen
// stands where the outside model's canopy does, and its reflector sight's
// glass straddles the boresight. This module hangs a camera on that eye,
// draws the office in its own pass, and paints the gunsight's pipper onto
// the glass where the rounds actually go.
//
// Why a pass of its own: the panel is 0.7 m from the eye and the stick and
// throttle closer still, but the world camera's near plane is 0.5 m (a 12 km
// map needs its depth precision). So the world renders first, exactly as it
// does from the chase camera, and then the office renders over it into a
// cleared depth buffer through a camera with a 2 cm near plane. Nothing in
// the world can stand between the eye and its own instrument panel, so
// "the office draws on top" is always the right answer.
//
// The office rides in a scene of its own, translated so the eye sits at the
// origin (millimetre parts don't want kilometre coordinates) but turned with
// the world, so its lights can be the sky's lights copied over each frame:
// the sun that lights a wing lights the coaming beside it, the dusk that
// reddens the clouds reddens the panel.
//
// The outside airframe keeps flying around the seat: wings, booms, engines,
// prop discs and muzzle flashes are its. Only the parts the office replaces
// are hidden (the canopy's glass and frame, and on the Fortress and the 109
// the pilot figures that would otherwise sit on our lap), plus the propeller
// blades: from behind the spinner a blade the renderer freezes 120 times a
// second is a black bar strobing across the windscreen, where a real prop is
// the faint disc that stays.
//
// The gunsight. A reflector sight projects its reticle at infinity: the
// pipper hangs over one DIRECTION, wherever the eye sits behind the glass,
// and you only see it where you look through the glass. So the reticle here
// is not a picture on the glass but a shader on it: every fragment of the
// sight glass asks "which way does the eye look through me?" and lights up
// if that way is on the ring, the range bars or the pip. The pipper's line
// is gunSightLine (gunnery.js): the direction from the eye to where the
// battery's rounds cross the harmonization range, a few mils under the
// eye's own boresight because the rounds converge on the NOSE line below
// the eye and fall on the way there. Put the pip on a bandit at convergence
// range, flying your way at your speed, and that is where the burst goes.

import * as THREE from "three";
import { loadModel, ModelInstance } from "./models.js";
import { gunSightLine } from "./gunnery.js";
import { OfficeGauges } from "./office_gauges.js";

// The offices, by the exterior model the class flies (aircraft_models.js
// CLASS_MODEL). `eye` is where the pilot's eye (the camera) sits in that
// model's frame; the office's origin sits there too (the manifest's "Origin"
// notes), unless `head` says the pilot sits a few centimetres off it (the
// eye's place in the office's own frame). `hide` names (prefix stripped) the
// outside model's parts the office stands in for, and the prop blades (see
// above); `sight` is the reflector sight's glass and the reticle it projects
// (radians, as seen from the eye), or null for an office with no gunsight,
// which keeps the HUD's cross. `name` is what the hangar calls the airframe
// in a sentence.
//
// Each reticle is sized to its glass: the pip rides ~9 mils under the eye's
// boresight, and the ring has to clear the glass's edges from there. A Revi
// (the two German offices) crosses its ring with a broken cross, as the
// Luftwaffe's did; the Allied sights keep their bars outside the ring. The
// Mustang's K-14 throws its gyro reticle instead: no ring line, but
// `diamonds` [count, radial half-length, half-width] stood round the ring's
// circle about the pip.
export const COCKPITS = {
  // The Spitfire's office, and its Barr & Stroud
  // reflector sight with the orange-red ring and the range bars either side.
  // The glass reaches 47 mils over the pip (38 over the eye's own line), so
  // the ring gets 38.
  spitfire: {
    name: "Spitfire",
    stem: "spitfire_cockpit",
    eye: [0.45, 0.68, 0],
    hide: /^(Canopy|CanopyFrame|Propeller)/,
    sight: {
      glass: "SightGlass",
      color: 0xff7a2e,
      ring: 0.038, // ring radius
      pip: 0.0022, // centre pip radius (never under ~2 px on screen)
      bars: [0.044, 0.064], // range bars, either side of the ring
      tick: [0.044, 0.056], // the drop line under the ring
    },
  },
  // The P-51D's office, and its K-14 gyro gunsight: six amber diamonds round
  // the pip, the reticle a Mustang pilot framed a bandit's wings in. Its glass
  // stands tall on the sight head, 50 mils of it under the pip and 68 over,
  // 82 either side: a 34-mil circle of diamonds, each 11 mils long, clears its
  // foot by 11.
  p51d: {
    name: "Mustang",
    stem: "p51d_cockpit",
    eye: [0.18, 0.73, 0],
    hide: /^(Canopy|CanopyFrame|Propeller)/,
    sight: {
      glass: "SightGlass",
      color: 0xff9a2e,
      ring: 0.034, // the diamonds' circle
      pip: 0.0022,
      diamonds: [6, 0.0055, 0.0032],
      bars: [0, 0],
      tick: [0, 0],
    },
  },
  // The P-38L's office, whose glass the boresight crosses near its
  // round top — 13 mils of glass above the eye's line, 23 above the pip. The
  // ring stays inside that and the bars run long instead.
  p38: {
    name: "P-38",
    stem: "p38_cockpit",
    eye: [0.6, 0.92, 0],
    hide: /^(Canopy|CanopyFrame|Propeller)/,
    sight: {
      glass: "SightGlass",
      color: 0xffb03a,
      ring: 0.019,
      pip: 0.0022,
      bars: [0.025, 0.056],
      tick: [0.025, 0.04],
    },
  },
  // The B-17's flight deck, from the left seat. A heavy's pilot has no
  // gunsight (the chin turret belongs to the bombardier), so the HUD's cross
  // stays, ranged from this seat. `Details` holds the outside model's two
  // pilot figures: the copilot would sit through the office's empty seat.
  b17: {
    name: "B-17",
    stem: "b17_cockpit",
    eye: [5.75, 1.325, -0.47],
    hide: /^(Canopy|CanopyFrame|Details|Propeller)/,
    sight: null,
  },
  // The Bf 109 G's office. Its eye sits 7.5 cm starboard of the centreline,
  // behind the Revi 16B that the drawing hangs right of the panel's middle,
  // and the outside model's pilot figure (a node of its own on this one)
  // steps out of the seat with the canopy. The Revi's glass is the roomiest
  // of the six, 54 mils of it round the pip: a 45-mil ring crossed by the
  // Revi's broken cross, amber as its lamp threw it. The sight is ranged for
  // the MK 108, whose slow round falls far enough by the harmonization that
  // the pip rides 15 mils under the eye, so the cross's lower arm stops at 47.
  bf109: {
    name: "Bf 109",
    stem: "bf109_cockpit",
    eye: [0.76, 0.81, 0.075],
    hide: /^(Canopy|CanopyFrame|Pilot|Propeller)/,
    sight: {
      glass: "SightGlass",
      color: 0xffc04a,
      ring: 0.045,
      pip: 0.0022,
      bars: [0.008, 0.062], // the cross's arms, broken round the pip
      tick: [0.008, 0.047],
    },
  },
  // The Fw 190 A-8's office, sat far back over the wing root. Its Revi 16B
  // leans low in the coaming: the glass reaches 76 mils over the pip but only
  // 33 under it, so the ring shrinks to 28 and the arms run long instead.
  fw190: {
    name: "Fw 190",
    stem: "fw190_cockpit",
    eye: [-0.25, 0.72, 0],
    hide: /^(Canopy|CanopyFrame|Propeller)/,
    sight: {
      glass: "SightGlass",
      color: 0xffc04a,
      ring: 0.028,
      pip: 0.0022,
      bars: [0.008, 0.06],
      tick: [0.008, 0.028],
    },
  },
  // The Corsair's office, a long way aft behind that nose. Its Mk 8 glass
  // hangs 3 degrees low, all of it under the office's own boresight, so the
  // pilot sits 3 cm lower in the seat than the office's origin, ducked to his
  // sight as a Corsair pilot was; from there the glass holds a 40-mil ring
  // with room all round, the range bars outside it.
  f4u: {
    name: "Corsair",
    stem: "f4u_cockpit",
    eye: [-1.27, 0.78, 0],
    head: [0, -0.03, 0],
    hide: /^(Canopy|CanopyFrame|Propeller)/,
    sight: {
      glass: "SightGlass",
      color: 0xff9a3a,
      ring: 0.04,
      pip: 0.0022,
      bars: [0.044, 0.06],
      tick: [0.01, 0.03], // the drop line, inside the ring
    },
  },
};

/** The office class `type` offers, flying `stem` — or null (chase only). */
export function cockpitFor(stem) {
  return (stem && COCKPITS[stem]) || null;
}

/**
 * Which of a roster's airframes have an office, as the HUD says it to a pilot
 * who pressed C in one that hasn't: "the Spitfire, P-38 and B-17 have one".
 * `stems` = the class → model table (planes.js CLASS_MODELS); an era roster
 * with no office of its own gives "".
 */
export function officeRoll(stems) {
  const names = Object.keys(COCKPITS)
    .filter((s) => stems.includes(s))
    .map((s) => COCKPITS[s].name);
  if (names.length < 2) return names.length ? `the ${names[0]} has one` : "";
  return `the ${names.slice(0, -1).join(", ")} and ${names.at(-1)} have one`;
}

// The world camera's ±FOV the view keeps (the office's own camera copies it
// every frame, punches and all). The 2 cm near plane is the manifest's; 30 m
// of far plane is the length of a Fortress's nose and then some.
const NEAR = 0.02;
const FAR = 30;

// The reflector glass's reticle, evaluated per fragment of the glass. `uC` is
// the pipper's line and `uR`/`uU` the reticle's right and up, in the office
// scene's frame (world-aligned, eye at the origin); p is where this
// fragment's line of sight lands on the reticle's plane at unit distance, so
// the reticle's radii are its angular sizes. Strokes are measured in screen
// pixels (fwidth), so the ring reads as a crisp ~1.5 px line whether the
// window is a phone or a 4K monitor.
const SIGHT_VERT = /* glsl */ `
varying vec3 vWorld;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}
`;
const SIGHT_FRAG = /* glsl */ `
uniform vec3 uC;
uniform vec3 uR;
uniform vec3 uU;
uniform vec3 uColor;
uniform float uAlpha;
uniform float uRing;
uniform float uPip;
uniform vec2 uBars;
uniform vec2 uTick;
uniform vec3 uDiam; // count, radial half-length, half-width (count 0: the ring is a line)
varying vec3 vWorld;

// 1 inside a stroke of half-width hw around d = 0, antialiased over aa.
float stroke(float d, float hw, float aa) {
  return 1.0 - smoothstep(hw - aa, hw + aa, d);
}
// 1 between lo and hi along x.
float span(float x, float lo, float hi, float aa) {
  return smoothstep(lo - aa, lo + aa, x) * (1.0 - smoothstep(hi - aa, hi + aa, x));
}

void main() {
  vec3 d = normalize(vWorld - cameraPosition);
  float z = dot(d, uC);
  if (z <= 0.0) discard;
  vec2 p = vec2(dot(d, uR), dot(d, uU)) / z;
  float px = max(fwidth(p.x), fwidth(p.y)) * 0.7; // ~one screen pixel, in reticle units
  float hw = 0.75 * px;
  float r = length(p);
  float a;
  if (uDiam.x > 0.5) {
    // The gyro sight's diamonds: the nearest of uDiam.x round the circle,
    // the first at 12 o'clock, each a rhombus pointing at the pip.
    float step = 6.2831853 / uDiam.x;
    float ca = floor(atan(p.x, p.y) / step + 0.5) * step;
    vec2 rad = vec2(sin(ca), cos(ca));
    vec2 q = p - rad * uRing;
    float dd = abs(dot(q, rad)) / uDiam.y + abs(dot(q, vec2(rad.y, -rad.x))) / uDiam.z;
    a = 1.0 - smoothstep(1.0 - px / uDiam.z, 1.0 + px / uDiam.z, dd);
  } else {
    a = stroke(abs(r - uRing), hw, px);
  }
  a = max(a, stroke(r, max(uPip, 1.6 * px), px));
  a = max(a, span(abs(p.x), uBars.x, uBars.y, px) * stroke(abs(p.y), hw, px));
  a = max(a, span(-p.y, uTick.x, uTick.y, px) * stroke(abs(p.x), hw, px));
  if (a < 0.004) discard;
  gl_FragColor = vec4(uColor, a * uAlpha);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/**
 * The reticle's frame for a pipper line `c` (unit, any frame) in an airframe
 * whose up is `up` (same frame): right and up across the line, square to it,
 * so the range bars stay level with the wings. Pure (tests read it).
 */
export function reticleBasis(c, up, outR, outU) {
  // up with its component along the line taken out
  const k = up.x * c.x + up.y * c.y + up.z * c.z;
  let ux = up.x - k * c.x, uy = up.y - k * c.y, uz = up.z - k * c.z;
  const n = Math.hypot(ux, uy, uz) || 1;
  ux /= n; uy /= n; uz /= n;
  outU.x = ux; outU.y = uy; outU.z = uz;
  // right = line x up
  outR.x = c.y * uz - c.z * uy;
  outR.y = c.z * ux - c.x * uz;
  outR.z = c.x * uy - c.y * ux;
  return outR;
}

// Scratch (one cockpit per page).
const _q = new THREE.Quaternion();
const _qHead = new THREE.Quaternion();
const _e = new THREE.Euler(0, 0, 0, "YXZ");
const _v = new THREE.Vector3();
const _up = new THREE.Vector3();
const _line = { x: 1, y: 0, z: 0 };
const _R = { x: 0, y: 0, z: 1 };
const _U = { x: 0, y: 1, z: 0 };
// A camera looks down its own -Z with +Y up; the airframe looks down +X with
// +Y up. This turns the one onto the other (a quarter turn about Y).
const CAM_TO_BODY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.PI / 2);

export class CockpitView {
  /** `lights` = the sky's { sun, hemi, ambient } (world.js buildSky parts). */
  constructor(lights) {
    this.lights = lights;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(72, 1, NEAR, FAR);
    this.sun = new THREE.DirectionalLight(0xffffff, 1);
    this.hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 1);
    this.ambient = new THREE.AmbientLight(0xffffff, 0.2);
    this.scene.add(this.sun, this.sun.target, this.hemi, this.ambient);
    this.root = new THREE.Group(); // the eye's frame: the airframe's attitude, at the origin
    this.scene.add(this.root);
    this.offices = new Map(); // stem → { inst, sight, gauges } once mounted
    this.loading = new Map(); // stem → Promise
    this.failed = new Set();
    this.shown = null; // the mounted office's spec
    this.line = new THREE.Vector3(1, 0, 0); // this frame's pipper line, world frame
    this.eye = new THREE.Vector3(); // this frame's eye, world frame
  }

  /** True once `spec`'s office is built and ready to draw. */
  ready(spec) {
    return !!spec && this.offices.has(spec.stem);
  }

  /** Fetch and build `spec`'s office (once). Resolves true when it's ready. */
  load(spec) {
    if (!spec) return Promise.resolve(false);
    if (this.offices.has(spec.stem)) return Promise.resolve(true);
    if (this.failed.has(spec.stem)) return Promise.resolve(false);
    let p = this.loading.get(spec.stem);
    if (!p) {
      p = loadModel(spec.stem)
        .then((tpl) => {
          this._build(spec, tpl);
          return true;
        })
        .catch(() => {
          this.failed.add(spec.stem);
          return false;
        })
        .finally(() => this.loading.delete(spec.stem));
      this.loading.set(spec.stem, p);
    }
    return p;
  }

  _build(spec, tpl) {
    const inst = new ModelInstance(tpl, { team: 0 });
    inst.root.visible = false;
    // The scene's origin is the eye; an office whose pilot sits off its own
    // origin (`head`) slides the other way under him.
    if (spec.head) inst.root.position.set(-spec.head[0], -spec.head[1], -spec.head[2]);
    // The office is close enough to touch: nothing in it may be culled by a
    // bounding sphere computed for a model seen from outside.
    inst.root.traverse((o) => {
      if (o.isMesh) o.frustumCulled = false;
    });
    this.root.add(inst.root);
    const sight = spec.sight ? this._buildSight(inst, spec.sight) : null;
    // The panel's needles, the horizon, the stick and the pedals (office_gauges.js).
    const gauges = new OfficeGauges((n) => inst.node(n), spec.stem);
    this.offices.set(spec.stem, { inst, sight, gauges });
  }

  // The pipper: a shader over the sight glass's own triangles (see the file
  // header), drawn after the glass so it lies on it.
  _buildSight(inst, s) {
    const node = inst.node(s.glass);
    let glass = null;
    node?.traverse((o) => {
      if (!glass && o.isMesh && o.material?.transparent) glass = o;
    });
    if (!glass) return null;
    const uniforms = {
      uC: { value: new THREE.Vector3(1, 0, 0) },
      uR: { value: new THREE.Vector3(0, 0, 1) },
      uU: { value: new THREE.Vector3(0, 1, 0) },
      uColor: { value: new THREE.Color(s.color) },
      uAlpha: { value: 0.95 },
      uRing: { value: s.ring },
      uPip: { value: s.pip },
      uBars: { value: new THREE.Vector2(s.bars[0], s.bars[1]) },
      uTick: { value: new THREE.Vector2(s.tick[0], s.tick[1]) },
      uDiam: { value: new THREE.Vector3(...(s.diamonds ?? [0, 0, 0])) },
    };
    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: SIGHT_VERT,
      fragmentShader: SIGHT_FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      extensions: { derivatives: true }, // fwidth on a WebGL1 context
    });
    const reticle = new THREE.Mesh(glass.geometry, mat);
    reticle.name = "cockpit-reticle";
    reticle.renderOrder = (glass.renderOrder ?? 0) + 1;
    reticle.frustumCulled = false;
    glass.parent.add(reticle);
    reticle.position.copy(glass.position);
    reticle.quaternion.copy(glass.quaternion);
    reticle.scale.copy(glass.scale);
    return { mesh: reticle, uniforms, spec: s };
  }

  /** Show `spec`'s office (null: none). */
  mount(spec) {
    if (this.shown === spec) return;
    for (const [stem, o] of this.offices) o.inst.root.visible = !!spec && stem === spec.stem;
    this.shown = spec;
    // Climbing back into the seat, the instruments read the flight at once
    // rather than swinging over from wherever the last sortie left them.
    if (spec) this.offices.get(spec.stem)?.gauges.reset();
  }

  /**
   * Once per frame in the seat: the mounted office's instruments and
   * controls read the flight (office_gauges.js OfficeGauges.update:
   * { speed, alt, vy, att, ctl, engine }), easing on the wall clock.
   */
  fly(flight, wallDt) {
    if (this.shown) this.offices.get(this.shown.stem)?.gauges.update(flight, wallDt);
  }

  /** The mounted office's reflector sight (null for a sightless office). */
  sight() {
    return this.shown ? (this.offices.get(this.shown.stem)?.sight ?? null) : null;
  }

  /**
   * Put the world camera in the seat: on the eye of the airframe at
   * (pos, att), looking down the boresight, turned by the head (freelook:
   * `look` is input.look — yaw right, pitch down, eased in and out by blend).
   * The world camera keeps its own near plane; only the office needs 2 cm.
   */
  poseCamera(camera, pos, att, look) {
    const spec = this.shown;
    if (!spec) return;
    this.eye.set(spec.eye[0], spec.eye[1], spec.eye[2]).applyQuaternion(att).add(pos);
    camera.position.copy(this.eye);
    const b = look?.blend ?? 0;
    // Head: yaw about the airframe's up (mouse right looks right, toward +Z),
    // then pitch about the turned right (mouse down looks down), both eased by
    // the freelook blend so letting go swings the head back to the sight.
    _e.set(b > 0 ? -look.pitch * b : 0, b > 0 ? -look.yaw * b : 0, 0, "YXZ");
    _qHead.setFromEuler(_e);
    // Euler YXZ here is in camera terms (x right, y up); the head turns the
    // camera in its own frame before the camera is laid on the airframe.
    camera.quaternion.copy(att).multiply(CAM_TO_BODY).multiply(_qHead);
    camera.up.set(0, 1, 0).applyQuaternion(att);
  }

  /**
   * The pipper's line for this frame: the direction, world frame, from the
   * eye to where the rounds cross the harmonization range (gunnery.js
   * gunSightLine). `gun` = { convergenceM, muzzleMS, tickDt, muzzle } (muzzle:
   * the sighting mount's mean barrel tip, battery.js sightingMuzzle).
   */
  aimLine(att, gun) {
    const spec = this.shown;
    if (!spec || !gun) return this.line.set(1, 0, 0).applyQuaternion(att);
    gunSightLine({ x: spec.eye[0], y: spec.eye[1], z: spec.eye[2] }, att, gun.convergenceM, gun.muzzleMS, gun.tickDt, _line, undefined, gun.muzzle);
    return this.line.set(_line.x, _line.y, _line.z).applyQuaternion(att);
  }

  /** The world point the pipper marks at range `m` (for the HUD cross in a sightless office). */
  aimPoint(m, out) {
    return out.copy(this.line).multiplyScalar(m).add(this.eye);
  }

  /**
   * Once per frame, after the world camera has its final pose (killcam punch
   * and all): lay the office on the airframe's attitude, copy the camera,
   * copy the sky's light, and turn the reticle onto this frame's line.
   */
  sync(camera, att) {
    this.root.quaternion.copy(att);
    const cam = this.camera;
    cam.quaternion.copy(camera.quaternion);
    if (cam.fov !== camera.fov || cam.aspect !== camera.aspect) {
      cam.fov = camera.fov;
      cam.aspect = camera.aspect;
      cam.updateProjectionMatrix();
    }
    cam.position.set(0, 0, 0);
    const L = this.lights;
    if (L) {
      this.sun.color.copy(L.sun.color);
      this.sun.intensity = L.sun.intensity;
      // Only the light's direction matters for a directional light: the sky
      // parks the sun 2 km out (tier 2 re-centres it on the view), so take
      // its bearing from its target and hang ours 10 m out the same way.
      _v.copy(L.sun.position).sub(L.sun.target.position);
      if (_v.lengthSq() > 1e-6) this.sun.position.copy(_v.normalize().multiplyScalar(10));
      this.hemi.color.copy(L.hemi.color);
      this.hemi.groundColor.copy(L.hemi.groundColor);
      this.hemi.intensity = L.hemi.intensity;
      this.ambient.color.copy(L.ambient.color);
      this.ambient.intensity = L.ambient.intensity;
    }
    const s = this.sight();
    if (s) {
      const u = s.uniforms;
      u.uC.value.copy(this.line);
      _up.set(0, 1, 0).applyQuaternion(att);
      reticleBasis(this.line, _up, _R, _U);
      u.uR.value.set(_R.x, _R.y, _R.z);
      u.uU.value.set(_U.x, _U.y, _U.z);
    }
  }

  /** Draw the office over a world the renderer just drew (tier 1). */
  renderOver(renderer) {
    const keep = renderer.autoClear;
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(this.scene, this.camera);
    renderer.autoClear = keep;
  }
}
